import Tooltip from "@components/ui/tooltip";
import { useAuthStoreShallow } from "@features/authentication";
import { cn } from "@lib/cn";
import { SlidesLibraryDialog } from "@modules/slides-library/shared/components/slides-library-dialog";
import { openMediaLibDialog } from "@pages/editor/modules/media-lib";
import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FUNNEL_PATHS } from "@/pages/how-to-start/funnel-paths";
import { usePlatformFunnelStore } from "@/pages/how-to-start/platform-funnel.store";
import { LIBRARIES_MENU } from "../constants";
import { useSidebarStoreShallow } from "../store";

export const SidebarLibrariesMenu = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const openFunnel = usePlatformFunnelStore((s) => s.openFunnel);
  const { pathname } = useLocation();
  const userType = useAuthStoreShallow((s) => s.userType);
  const { isOpen } = useSidebarStoreShallow((s) => ({
    isOpen: s.isOpen,
  }));

  const handleClick = useCallback(
    (action: string) => {
      if (action === "media") {
        openMediaLibDialog({ source: "settings" });
        return;
      }
      if (action === "slides") {
        dispatch(
          openDialog({
            id: SlidesLibraryDialog.id,
            content: SlidesLibraryDialog,
            props: { view: "library" },
          }),
        );
        return;
      }
      if (action === "templates") {
        openFunnel(FUNNEL_PATHS.templates);
        return;
      }
      if (action === "design") {
        navigate("/pages/design");
        return;
      }
      if (action === "variables") {
        navigate("/pages/variables");
        return;
      }
      // design: stub for now
    },
    [dispatch, navigate, openFunnel],
  );

  return (
    <div
      data-tour="libraries-nav"
      className={cn(
        "tw-flex tw-flex-col tw-px-4 tw-py-2 tw-transition-all tw-duration-300",
        !isOpen && "tw-px-2",
      )}
    >
      {isOpen && (
        <p className="tw-flex tw-h-8 tw-items-center tw-px-2 tw-font-sans tw-text-xs tw-font-normal tw-text-blue/70">
          Libraries
        </p>
      )}
      <div className="tw-space-y-1">
        {LIBRARIES_MENU.map((item) => {
          const Icon = item.icon;
          if (item.hiddenFor && item.hiddenFor.includes(userType ?? "")) {
            return null;
          }
          const isActive = !!item.path && pathname.includes(item.path);
          return (
            <div key={item.action} className="tw-relative tw-w-full">
              <Tooltip placement="right" offset={10}>
                <Tooltip.Trigger asChild>
                  <button
                    onClick={() => handleClick(item.action)}
                    type="button"
                    className={cn(
                      "tw-flex tw-h-8 tw-w-full tw-items-center tw-justify-start tw-overflow-hidden tw-rounded-lg tw-p-2 tw-outline-none tw-transition-all tw-duration-300 hover:tw-bg-blue-100/50",
                      {
                        "tw-bg-blue-100 hover:tw-bg-blue-100": isActive,
                        "tw-gap-0": !isOpen,
                      },
                    )}
                  >
                    <Icon
                      className={cn(
                        "tw-size-4 tw-shrink-0 tw-text-blue tw-transition-all tw-duration-300",
                        !isActive && "tw-opacity-50",
                      )}
                    />
                    <span
                      className={cn(
                        "tw-text-sans tw-ml-2 tw-overflow-hidden tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-blue tw-transition-all tw-duration-300 tw-ease-in-out",
                        !isOpen &&
                          "tw-pointer-events-none tw-ml-0 !tw-w-0 tw-opacity-0",
                        !isActive && "tw-opacity-50",
                      )}
                    >
                      {item.label}
                    </span>
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Content className={cn(isOpen && "tw-hidden")}>
                  <Tooltip.Text className="tw-rounded-md tw-bg-primary tw-px-3 tw-py-2 tw-text-sm tw-text-white">
                    {item.label}
                  </Tooltip.Text>
                </Tooltip.Content>
              </Tooltip>
              {isActive && (
                <span
                  className={cn(
                    "tw-pointer-events-none tw-absolute -tw-right-4 tw-top-0 tw-z-10 tw-h-8 tw-w-1 tw-rounded-l-lg tw-bg-primary",
                    !isOpen && "-tw-right-2",
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
